import { I18n } from 'react-redux-i18n'
import * as API from '../utility/api'

let cachedRules = null
const rulesCache = new Map()
const promiseCache = new Map()

const LEGACY_RULES = {
  seller_share_rate: 0.9,
  organizer_commission_rate: 0.05,
  buyer_service_fee_rate: 0,
  buyer_service_fee_vat_rate: 0,
  feature_active: false
}

const roundCents = value => Math.round(value)

export const getEffectivePricingRules = rules => ({
  ...LEGACY_RULES,
  ...(rules || {})
})

export const isBuyerServiceFeeActive = rules =>
  Boolean(rules && rules.feature_active && rules.buyer_service_fee_rate > 0)

export const shouldShowResaleTermsModal = rules =>
  Boolean(rules && rules.enabled)

export const calculateBuyerPrice = (sum, rules) => {
  const base = parseInt(sum, 10) || 0

  if (!isBuyerServiceFeeActive(rules)) {
    return base
  }

  return Math.round(base * (1 + rules.buyer_service_fee_rate))
}

export const calculateSellerNetPrice = (sum, rules) => {
  const base = parseInt(sum, 10) || 0
  const effectiveRules = getEffectivePricingRules(rules)
  return roundCents(base * effectiveRules.seller_share_rate)
}

export const calculatePricingBreakdown = (sum, rules) => {
  const resalePrice = parseInt(sum, 10) || 0
  const effectiveRules = getEffectivePricingRules(rules)
  const buyerServiceFeeAmount = isBuyerServiceFeeActive(effectiveRules)
    ? roundCents(resalePrice * effectiveRules.buyer_service_fee_rate)
    : 0
  const buyerServiceFeeExVatAmount =
    buyerServiceFeeAmount > 0
      ? roundCents(
          buyerServiceFeeAmount / (1 + effectiveRules.buyer_service_fee_vat_rate)
        )
      : 0
  const buyerServiceFeeVatAmount =
    buyerServiceFeeAmount - buyerServiceFeeExVatAmount

  return {
    resale_price_amount: resalePrice,
    seller_net_amount: calculateSellerNetPrice(resalePrice, effectiveRules),
    organizer_commission_amount: roundCents(
      resalePrice * effectiveRules.organizer_commission_rate
    ),
    buyer_service_fee_amount: buyerServiceFeeAmount,
    buyer_service_fee_vat_amount: buyerServiceFeeVatAmount,
    buyer_total_amount: resalePrice + buyerServiceFeeAmount
  }
}

export const buyerServiceFeeLabel = rules => {
  if (!isBuyerServiceFeeActive(rules)) {
    return ''
  }

  const feePercent = Math.round(rules.buyer_service_fee_rate * 100)
  const vatPercent = (rules.buyer_service_fee_vat_rate * 100).toFixed(1)
  return I18n.t('tickets.service_fee_included_detail', {
    fee: feePercent,
    vat: vatPercent
  })
}

export const sellerOwnShareDescription = rules => {
  const effectiveRules = getEffectivePricingRules(rules)
  const sellerSharePercent = Math.round(effectiveRules.seller_share_rate * 100)
  const organizerCommissionPercent = Math.round(
    effectiveRules.organizer_commission_rate * 100
  )

  if (effectiveRules.feature_active) {
    return I18n.t('tickets.own_share_description_v2', {
      seller_share: sellerSharePercent,
      organizer_commission: organizerCommissionPercent
    })
  }

  return I18n.t('tickets.own_share_description_legacy', {
    seller_share: sellerSharePercent
  })
}

export const loadResalePricingRules = (asOf) => {
  const cacheKey = asOf || 'current'

  if (rulesCache.has(cacheKey)) {
    return Promise.resolve(rulesCache.get(cacheKey))
  }

  if (promiseCache.has(cacheKey)) {
    return promiseCache.get(cacheKey)
  }

  const query = asOf ? `?as_of=${encodeURIComponent(asOf)}` : ''
  const promise = API.GET(`pricing/resale${query}`)
    .then(({ data }) => {
      rulesCache.set(cacheKey, data.pricing)
      if (!asOf) {
        cachedRules = data.pricing
      }
      return data.pricing
    })
    .catch(() => {
      promiseCache.delete(cacheKey)
      return null
    })

  promiseCache.set(cacheKey, promise)

  return promise
}

export const calculateBasketPricingBreakdown = (tickets, rulesByDate = {}) => {
  return tickets.reduce(
    (acc, ticket) => {
      const rules =
        rulesByDate[ticket.sale_start_date] ||
        rulesByDate.current ||
        null
      const ticketBreakdown = calculatePricingBreakdown(ticket.price, rules)

      return {
        resale_price_amount:
          acc.resale_price_amount + ticketBreakdown.resale_price_amount,
        seller_net_amount:
          acc.seller_net_amount + ticketBreakdown.seller_net_amount,
        organizer_commission_amount:
          acc.organizer_commission_amount +
          ticketBreakdown.organizer_commission_amount,
        buyer_service_fee_amount:
          acc.buyer_service_fee_amount + ticketBreakdown.buyer_service_fee_amount,
        buyer_service_fee_vat_amount:
          acc.buyer_service_fee_vat_amount +
          ticketBreakdown.buyer_service_fee_vat_amount,
        buyer_total_amount:
          acc.buyer_total_amount + ticketBreakdown.buyer_total_amount
      }
    },
    {
      resale_price_amount: 0,
      seller_net_amount: 0,
      organizer_commission_amount: 0,
      buyer_service_fee_amount: 0,
      buyer_service_fee_vat_amount: 0,
      buyer_total_amount: 0
    }
  )
}

export const loadResalePricingRulesForTickets = (tickets = []) => {
  const dates = [
    ...new Set(
      tickets.map(ticket => ticket.sale_start_date).filter(Boolean)
    )
  ]

  return Promise.all([
    loadResalePricingRules(),
    ...dates.map(date => loadResalePricingRules(date))
  ]).then(() => {
    const rulesByDate = { current: rulesCache.get('current') || cachedRules }

    dates.forEach(date => {
      rulesByDate[date] = rulesCache.get(date)
    })

    return rulesByDate
  })
}
